import api from "../api";
import {
  trackBookingArrived,
  trackBookingCancelled,
  trackBookingCompleted,
  trackBookingCreated,
  trackBookingDelivered,
  trackBookingRideStarted,
} from "../analytics-events";
import { appendFileToFormData } from "../form-data";
import { CreateBookingData } from "../validation/booking";
import { PriceEstimate } from "./pricing";
import { Quote } from "./quotes";

export interface Waypoint {
  id?: number;
  address: string;
  latitude: number;
  longitude: number;
  stop_order: number;
  contact_name?: string;
  contact_phone?: string;
  special_instructions?: string;
}

interface Customer {
  accepts_cash: boolean;
  avatar: string;
  can_quote?: boolean;
  email: string;
  id: number;
  is_top_rated: boolean;
  loyalty_tier?: string;
  name: string;
  phone: string;
  reputation_score: string;
}
interface Pricing {
  callout_fee?: number;
  per_mile_rate?: number;
  waiting_time_charge?: number;
  storage_charge?: number;
  toll_charge?: number;
  ulez_charge?: number;
  surge_multiplier?: number;
  total_amount?: number;
  service_charge?: number;
  tip_amount?: number;
  operator_service_net?: number;
  operator_vat_amount?: number;
  operator_service_gross_amount?: number;
  at_fee_net?: number;
  at_vat_amount?: number;
  tax_review_flag?: boolean;
}

export type JobCategory = "live" | "scheduled" | "quote_only";

interface Payment {
  id: number;
  status: string;
  amount: number;
  tip_amount: number;
  authorized_at?: string;
  captured_at?: string;
}

export interface Booking {
  id: number;
  service_type: string;
  service_type_label: string;
  status: string;
  status_label: string;
  payment_method?: "card" | "cash";
  from: {
    latitude: number;
    longitude: number;
  };
  from_address: string;
  from_area?: string | null;
  to: {
    latitude: number;
    longitude: number;
  };
  customer_id: number;
  driver_id: number;
  to_address: string;
  to_area?: string | null;
  vehicle_registration: string;
  vehicle_make?: string;
  vehicle_model?: string;
  vehicle_colour?: string;
  vehicle_fuel_type?: string;
  vehicle_year?: number;
  vehicle_image_path?: string;
  vehicle_body_type?: string;
  vehicle_category?: string;
  vehicle_weight?: string;
  vehicle_mot_status?: string;
  vehicle_expiry_date?: string;
  vehicle_tax_status?: string;
  vehicle_tax_due_date?: string;
  vehicle_count?: number;
  passenger_count?: number;
  pet_count?: number;
  storage_days?: number;
  is_premium?: boolean;
  notes?: string;
  scheduled_at?: string;
  job_category?: JobCategory;
  job_category_label?: string;
  distance_miles: number;
  estimated_duration_minutes: number;
  estimated_price?: number;
  total_amount?: number;
  pricing?: Pricing;
  customer?: Customer;
  driver?: Customer;
  vehicle?: any;
  quotes?: Quote[];
  waypoints?: Waypoint[];
  accepted_quote?: any;
  started_at?: string;
  completed_at?: string;
  cancelled_at?: string;
  cancellation_reason?: string;
  driver_latitude?: string;
  driver_longitude?: string;
  payment?: Payment;
  user_rating?: {
    id: number;
    rating: number;
    comment?: string;
    type: string;
    created_at: string;
  } | null;
  questions?: any[];
  inspection_required: boolean;
  inspection_tier: string | null;
  inspection_status: string | null;
  inspection_price_snapshot: string;
  inspection_hold_reason: null;
  created_at: string;
  updated_at: string;
}

export interface UpdateBookingData {
  from_address?: string;
  from_latitude?: number;
  from_longitude?: number;
  to_address?: string;
  to_latitude?: number;
  to_longitude?: number;
  scheduled_at?: string;
  notes?: string;
}

const formatVehicleImage = async (data: CreateBookingData) => {
  const formData = new FormData();

  for (const [key, value] of Object.entries(data)) {
    if (value === undefined || value === null || key === "questions") {
      continue;
    }

    if (key === "vehicle_image") {
      await appendFileToFormData(
        formData,
        "vehicle_image",
        data.vehicle_image!,
      );
    } else if (Array.isArray(value)) {
      formData.append(key, JSON.stringify(value));
    } else if (typeof value === "boolean") {
      formData.append(key, value ? "1" : "0");
    } else {
      formData.append(key, value.toString());
    }
  }

  if (data.questions && Array.isArray(data.questions)) {
    data.questions.forEach((questionItem, index) => {
      Object.entries(questionItem).forEach(([questionKey, questionValue]) => {
        if (Array.isArray(questionValue)) {
          questionValue.forEach((val, i) => {
            formData.append(
              `questions[${index}][${questionKey}][]`,
              (val ?? "").toString(),
            );
          });
        } else if (typeof questionValue === "boolean") {
          formData.append(
            `questions[${index}][${questionKey}]`,
            questionValue ? "1" : "0",
          );
        } else if (questionValue !== null && questionValue !== undefined) {
          formData.append(
            `questions[${index}][${questionKey}]`,
            questionValue as string,
          );
        }
      });
    });
  }
  return formData;
};

class BookingService {
  async getBookings(params?: {
    status?: string;
    service_type?: string;
    page?: number;
  }): Promise<{ data: Booking[]; meta: any }> {
    const response = await api.get("/bookings", { params });
    return response.data;
  }

  async getBooking(id: number): Promise<Booking> {
    const response = await api.get(`/bookings/${id}`);
    return response.data.data;
  }

  async getBookingTrack(id: number): Promise<{
    booking: Booking;
  }> {
    const response = await api.get(`/bookings/${id}/track`);
    return response.data.data;
  }

  async bookingEstimate(data: CreateBookingData): Promise<PriceEstimate> {
    if (data.vehicle_image) {
      const formData = await formatVehicleImage(data);
      const response = await api.post("/bookings/estimate", formData);
      return response.data.data;
    }

    const response = await api.post("/bookings/estimate", data);
    return response.data.data;
  }

  async createBooking(data: CreateBookingData): Promise<Booking> {
    if (data.vehicle_image) {
      const formData = await formatVehicleImage(data);
      const response = await api.post("/bookings", formData);
      const booking = response.data.data;
      trackBookingCreated(booking);
      return booking;
    }

    const response = await api.post("/bookings", data);
    const booking = response.data.data;
    trackBookingCreated(booking);
    return booking;
  }

  async updateBooking(id: number, data: UpdateBookingData): Promise<Booking> {
    const response = await api.put(`/bookings/${id}`, data);
    return response.data.data;
  }

  async cancelBooking(id: number, reason: string): Promise<Booking> {
    const response = await api.post(`/bookings/${id}/cancel`, { reason });
    trackBookingCancelled(id);
    return response.data.data;
  }

  async markAsArrived(id: number): Promise<Booking> {
    const response = await api.post(`/bookings/${id}/mark-as-arrived`);
    trackBookingArrived(id);
    return response.data.data;
  }

  async startRide(id: number): Promise<Booking> {
    const response = await api.post(`/bookings/${id}/start-ride`);
    trackBookingRideStarted(id);
    return response.data.data;
  }

  async markAsDelivered(id: number): Promise<Booking> {
    const response = await api.post(`/bookings/${id}/mark-as-delivered`);
    trackBookingDelivered(id);
    return response.data.data;
  }

  async completeBooking(
    id: number,
    verificationCode: string,
  ): Promise<Booking> {
    const response = await api.post(`/bookings/${id}/complete`, {
      verification_code: verificationCode,
    });
    trackBookingCompleted(id);
    return response.data.data;
  }

  async trackBooking(id: number): Promise<{
    booking: Booking;
    driver_location: {
      latitude: number;
      longitude: number;
      updated_at: string;
    } | null;
  }> {
    const response = await api.get(`/bookings/${id}/track`);
    return response.data;
  }

  async preparePayment(
    bookingId: number,
    useWalletBalance = false,
  ): Promise<{
    client_secret: string;
    payment_intent_id: string;
    customer_id: string;
    ephemeral_key: string;
    amount: number;
    wallet_amount_gbp?: number;
  }> {
    const response = await api.post("/payments/prepare", {
      booking_id: bookingId,
      use_wallet_balance: useWalletBalance,
    });
    return response.data;
  }

  async authorizePayment(
    bookingId: number,
    paymentIntentId: string,
  ): Promise<any> {
    const response = await api.post("/payments/authorize", {
      booking_id: bookingId,
      payment_intent_id: paymentIntentId,
    });
    return response.data;
  }

  async searchNearbyDrivers(bookingId: number): Promise<any> {
    const response = await api.post(
      `/bookings/${bookingId}/search-nearby-drivers`,
    );
    return response.data;
  }

  async recordVehicleRestartOutcome(
    bookingId: number,
    vehicleRestarted: boolean,
  ): Promise<{ data: { vehicle_restarted: boolean } }> {
    const response = await api.post(
      `/bookings/${bookingId}/vehicle-restart-outcome`,
      { vehicle_restarted: vehicleRestarted },
    );
    return response.data;
  }

  async convertToRecovery(
    bookingId: number,
    payload?: {
      reason?: string;
      from_address?: string;
      from_latitude?: number;
      from_longitude?: number;
      to_address?: string;
      to_latitude?: number;
      to_longitude?: number;
    },
  ): Promise<{
    data: { original_booking_id: number; recovery_booking_id: number };
  }> {
    const response = await api.post(
      `/bookings/${bookingId}/convert-to-recovery`,
      payload ?? {},
    );
    return response.data;
  }
}

export default new BookingService();
